export default function FAQSection() {
  const faqs = [
    {
      q: 'What is OOPBuy Spreadsheet?',
      a: 'OOPBuy Spreadsheet is a structured shopping system where product listings are organized in spreadsheet format. Users browse categorized product sheets, select items with full pricing visibility, and submit orders through an agent workflow. This approach provides more transparency compared to traditional agent-based shopping, as all product details, prices, and availability are visible upfront in the spreadsheet before you commit to purchasing.',
    },
    {
      q: 'Is OOPBuy Spreadsheet safe to use?',
      a: 'The OOPBuy Spreadsheet system incorporates multiple safety measures including verified source channels, secure payment processing, and documented order tracking. The spreadsheet format provides a clear audit trail for every transaction. However, users should always access spreadsheets through official verified channels and avoid unverified links. The system itself does not process payments directly — all transactions are handled through the secure main platform.',
    },
    {
      q: 'How does spreadsheet shopping work?',
      a: 'Spreadsheet shopping works by organizing product catalogs into structured digital spreadsheets. You browse the spreadsheet to find products you want, copy the product link or item ID, and submit your order request through the agent system. The agent then confirms pricing and availability, processes your payment securely, and coordinates international shipping with tracking updates provided throughout the process.',
    },
    {
      q: 'Do I need experience to use it?',
      a: 'No prior experience is needed to use the OOPBuy Spreadsheet system. The spreadsheet format is intuitive and straightforward — if you can browse a product catalog and fill out basic information, you can use the system. First-time users can start with the step-by-step guide available on this site to learn the complete workflow from browsing to receiving your order.',
    },
    {
      q: 'What is the difference between an agent and a spreadsheet system?',
      a: 'A traditional agent requires back-and-forth communication to get product information, pricing, and availability — often with delays and unclear pricing. A spreadsheet system like OOPBuy puts all product data directly in front of you in an organized format. You see prices, options, and availability immediately without waiting for agent responses. The agent role shifts from information provider to order processor, making the entire workflow faster and more transparent.',
    },
  ];

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://oopbuyspreadsheets.cyou';

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a,
      },
    })),
  };

  return (
    <section className="py-24 md:py-32 bg-background-100">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <div className="max-w-3xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1.5 rounded-full bg-accent-100 text-accent-800 text-xs font-semibold mb-6">
            Frequently Asked
          </span>
          <h2 className="text-3xl md:text-5xl font-heading font-bold text-foreground-950 mb-4">
            Common Questions About OOPBuy Spreadsheet
          </h2>
          <p className="text-foreground-600 text-sm max-w-lg mx-auto leading-relaxed">
            Everything you need to know about the spreadsheet-based shopping system before getting started.
          </p>
        </div>

        {/* FAQ Items */}
        <div className="space-y-4">
          {faqs.map((faq, idx) => (
            <details
              key={idx}
              className="group rounded-2xl bg-background-50 border border-background-200/70 overflow-hidden"
            >
              <summary className="flex items-center justify-between px-7 py-5 cursor-pointer list-none">
                <h3 className="text-base font-bold text-foreground-950 pr-6">
                  {faq.q}
                </h3>
                <span className="flex-shrink-0 w-8 h-8 rounded-full bg-accent-100 flex items-center justify-center group-open:bg-accent-200 transition-colors">
                  <i className="ri-add-line text-accent-700 group-open:hidden"></i>
                  <i className="ri-subtract-line text-accent-700 hidden group-open:block"></i>
                </span>
              </summary>
              <div className="px-7 pb-6">
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {faq.a}
                </p>
              </div>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}